import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  ChatToolType,
  ChatEffort,
  type ChatAgentCapabilitiesQuery,
  type SiriusMcpCategory,
} from "_graphql-types-frontend/graphql";
import { useRangerChatStore } from "../chatStore";
import { TOOL_CATEGORIES } from "../availableTools";
import {
  BENCHMARK_SOURCE_URL,
  CHAT_AGENT_CAPABILITIES,
  LLM_MODELS,
  limitedFeatures,
  modelStats,
  servesChat,
  VALUE_EXPLAINED,
  chatEffortOf,
  effortLabel,
  type EffortOf,
} from "../graphql";
import {
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Checkbox,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import type { PopoverActions } from "@mui/material/Popover";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

type ToolDisplay = {
  tool: ChatToolType;
  name: string;
  description: string;
};

// Extended descriptions for tools that have sub-capabilities.
//
// Sirius MCP is deliberately absent: its breakdown is the server's own
// category grouping (chatAgentCapabilities.mcpToolCategories), so adding a
// tool updates this line instead of leaving a stale hand-written list. The
// rest are single tools whose sub-capabilities are not a registry.
const TOOL_EXTENDED_INFO: Partial<Record<ChatToolType, string>> = {
  [ChatToolType.AlphaVantageMcp]:
    "Stock quotes, forex exchange rates, technical indicators",
  [ChatToolType.AtlasTool]:
    "Dynamic problem-solving, data analysis, report generation, web data retrieval, file transformation",
  [ChatToolType.SpawnSubAgentTool]:
    "Delegates tasks to focused child agents with their own tools and model",
  [ChatToolType.StageMutationsTool]:
    "Stage reviewable data changes: update investments, add performance, AUM, documents",
  [ChatToolType.ModifyMutationPlanTool]:
    "Edit staged data update inputs before approval",
};

/**
 * Presentation only. Which models exist comes from the server (`llmModels`);
 * a model with no blurb still renders its name.
 */
const EFFORT_OPTIONS = [ChatEffort.Low, ChatEffort.Medium, ChatEffort.High, ChatEffort.XHigh].map(value => ({
  value,
  label: effortLabel(value),
}));

/** The picked model's benchmarks and what it gets less of, under the picker. */
function ModelSummary({
  model,
  peers,
  effortOf,
}: {
  model: Parameters<typeof modelStats>[0] &
    Parameters<typeof limitedFeatures>[0];
  peers: Parameters<typeof modelStats>[1];
  effortOf: EffortOf;
}) {
  const stats = modelStats(model, peers, effortOf);
  const limited = limitedFeatures(model);
  if (!stats && limited.length === 0) return null;
  return (
    <div style={{ fontSize: 10, color: "#777", marginTop: 4 }}>
      {stats && (
        <div title={VALUE_EXPLAINED}>
          {stats}{" "}
          <Link
            href={BENCHMARK_SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 9, color: "#999" }}
          >
            (Artificial Analysis)
          </Link>
        </div>
      )}
      {limited.length > 0 && <div>Limited here: {limited.join(", ")}</div>}
    </div>
  );
}

export function ToolsTab({
  availableToolsDisplay,
}: {
  availableToolsDisplay: ToolDisplay[];
}) {
  const disabledTools = useRangerChatStore(s => s.disabledTools);
  const setDisabledTools = useRangerChatStore(s => s.setDisabledTools);
  const model = useRangerChatStore(s => s.model);
  const setModel = useRangerChatStore(s => s.setModel);
  const effort = useRangerChatStore(s => s.effort);
  const setEffort = useRangerChatStore(s => s.setEffort);

  const { data: modelsData } = useQuery(LLM_MODELS, {
    fetchPolicy: "cache-first",
  });
  const models = (modelsData?.llmModels ?? []).filter(servesChat);
  // A stored key the server no longer offers falls back to the default row.
  const selected =
    models.find(m => m.key === model) ?? models.find(m => m.isDefault);
  const shownEffort = effort ?? chatEffortOf(selected?.defaultEffort);
  // Benchmarks at the effort each model would run at: the pick, for a model
  // that takes one, else its default where we know it.
  const effortOf: EffortOf = m => {
    const row = models.find(r => r.key === m.key);
    if (!row?.supportsEffort) return null;
    return effort ?? chatEffortOf(row.defaultEffort);
  };

  const availableToolSet = useMemo(
    () => new Set(availableToolsDisplay.map(t => t.tool)),
    [availableToolsDisplay]
  );

  const toolNameMap = useMemo(() => {
    const map: Record<string, { name: string; description: string }> = {};
    for (const t of availableToolsDisplay) {
      map[t.tool] = { name: t.name, description: t.description };
    }
    return map;
  }, [availableToolsDisplay]);

  // The MCP breakdown comes from the server so it cannot drift from what the
  // tools actually declare. Absent (still loading, or an older server) simply
  // means no "Includes:" line rather than a wrong one.
  const { data: capsData } = useQuery<ChatAgentCapabilitiesQuery>(
    CHAT_AGENT_CAPABILITIES,
    { fetchPolicy: "cache-first" }
  );
  // Sirius MCP's own categories, from the registry's grouping. Shown as
  // checkboxes under the Sirius MCP row, all on by default; unticking one
  // leaves its tools out of the turn (excludedMcpCategories).
  const mcpCategories = useMemo(
    () =>
      (capsData?.chatAgentCapabilities?.mcpToolCategories ?? []).filter(
        c => c.tools.length > 0
      ),
    [capsData]
  );
  const excludedMcpCategories = useRangerChatStore(
    s => s.excludedMcpCategories
  );
  const setExcludedMcpCategories = useRangerChatStore(
    s => s.setExcludedMcpCategories
  );
  const toggleMcpCategory = (key: SiriusMcpCategory) =>
    setExcludedMcpCategories(
      excludedMcpCategories.includes(key)
        ? excludedMcpCategories.filter(k => k !== key)
        : [...excludedMcpCategories, key]
    );

  const categories = useMemo(() => {
    return TOOL_CATEGORIES.map(category => {
      const categoryTools = category.tools.filter(t => availableToolSet.has(t));
      if (categoryTools.length === 0) return null;
      return {
        key: category.key,
        title: category.title,
        tools: categoryTools.map(toolType => ({
          key: toolType,
          name: toolNameMap[toolType]?.name || toolType,
          description: toolNameMap[toolType]?.description || "",
          extendedInfo: TOOL_EXTENDED_INFO[toolType],
        })),
      };
    }).filter(Boolean) as {
      key: string;
      title: string;
      tools: {
        key: ChatToolType;
        name: string;
        description: string;
        extendedInfo?: string;
      }[];
    }[];
  }, [availableToolSet, toolNameMap]);

  const checkedSet = useMemo(
    () =>
      new Set(
        availableToolsDisplay
          .map(t => t.tool)
          .filter(t => !disabledTools?.includes(t))
      ),
    [availableToolsDisplay, disabledTools]
  );

  // Recompute disabledTools from the next checked set (mirrors antd Tree onCheck).
  const applyChecked = (nextChecked: Set<ChatToolType>) => {
    const allToolTypes = availableToolsDisplay.map(t => t.tool);
    setDisabledTools(allToolTypes.filter(t => !nextChecked.has(t)));
  };

  const toggleTool = (toolType: ChatToolType) => {
    const next = new Set(checkedSet);
    if (next.has(toolType)) next.delete(toolType);
    else next.add(toolType);
    applyChecked(next);
  };

  const toggleCategory = (tools: { key: ChatToolType }[]) => {
    const next = new Set(checkedSet);
    const allChecked = tools.every(t => next.has(t.key));
    tools.forEach(t => (allChecked ? next.delete(t.key) : next.add(t.key)));
    applyChecked(next);
  };

  // The chat panel resizes on focus, even with this menu open (opening it
  // focuses the panel). MUI sizes and places the menu from the field only
  // when it opens, so watch the field itself: on any change of its width, cap
  // the menu at that width and move it back under the field.
  const fieldRef = useRef<HTMLDivElement>(null);
  const menuActions = useRef<PopoverActions>(null);
  const [fieldWidth, setFieldWidth] = useState<number>();
  const measureField = () => setFieldWidth(fieldRef.current?.clientWidth);
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    const observer = new ResizeObserver(measureField);
    observer.observe(field);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    menuActions.current?.updatePosition();
  }, [fieldWidth]);

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
          Model
        </div>
        <TextField
          select
          id="chat-model-select"
          ref={fieldRef}
          size="small"
          fullWidth
          value={selected?.key ?? ""}
          onChange={e => setModel(e.target.value)}
          SelectProps={{
            onOpen: measureField,
            MenuProps: { action: menuActions, PaperProps: { style: { maxWidth: fieldWidth } } },
            renderValue: key =>
              models.find(m => m.key === key)?.displayName ?? "",
          }}
        >
          {models.map(m => (
            <MenuItem key={m.key} value={m.key} sx={{ display: "block", whiteSpace: "normal" }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                {m.displayName}
              </div>
              <div style={{ fontSize: 10, color: "#888" }}>
                {[m.description, modelStats(m, models, effortOf)]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </MenuItem>
          ))}
        </TextField>
        {selected && <ModelSummary model={selected} peers={models} effortOf={effortOf} />}
      </Box>
      {/* Only for a model that takes an effort level; others run at their own default. */}
      {selected?.supportsEffort && (
        <Box sx={{ mb: 2 }}>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
            Effort
          </div>
          <ToggleButtonGroup
            value={shownEffort}
            exclusive
            onChange={(_, value) => {
              if (value) setEffort(value as ChatEffort);
            }}
            size="small"
            fullWidth
          >
            {EFFORT_OPTIONS.map(opt => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ textTransform: "none", fontSize: 11, py: 0.5 }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
        Toggle tools available to Sirius AI.
      </div>
      <SimpleTreeView
        defaultExpandedItems={[
          ...categories.map(c => c.key),
          ChatToolType.SiriusMcp,
        ]}
        disableSelection
        aria-label="tools-tree"
      >
        {categories.map(cat => {
          const catChecked = cat.tools.every(t => checkedSet.has(t.key));
          const catIndeterminate =
            !catChecked && cat.tools.some(t => checkedSet.has(t.key));
          return (
            <TreeItem
              key={cat.key}
              itemId={cat.key}
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    sx={{ p: 0.5 }}
                    checked={catChecked}
                    indeterminate={catIndeterminate}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleCategory(cat.tools)}
                  />
                  <Typography
                    component="span"
                    sx={{ fontSize: 12, fontWeight: 600 }}
                  >
                    {cat.title}
                  </Typography>
                </Box>
              }
            >
              {cat.tools.map(tool => (
                <TreeItem
                  key={tool.key}
                  itemId={tool.key}
                  label={
                    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={checkedSet.has(tool.key)}
                        indeterminate={
                          tool.key === ChatToolType.SiriusMcp &&
                          checkedSet.has(tool.key) &&
                          mcpCategories.some(c =>
                            excludedMcpCategories.includes(c.key)
                          )
                        }
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggleTool(tool.key)}
                      />
                      <span>
                        <Typography component="span" sx={{ fontSize: 12 }}>
                          {tool.name}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 10,
                            display: "block",
                            lineHeight: "14px",
                            color: "text.secondary",
                          }}
                        >
                          {tool.description}
                        </Typography>
                        {tool.extendedInfo && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 10,
                              fontStyle: "italic",
                              display: "block",
                              lineHeight: "14px",
                              color: "text.secondary",
                            }}
                          >
                            Includes: {tool.extendedInfo}
                          </Typography>
                        )}
                      </span>
                    </Box>
                  }
                >
                  {tool.key === ChatToolType.SiriusMcp &&
                    mcpCategories.map(mc => (
                      <TreeItem
                        key={mc.key}
                        itemId={`mcp-${mc.key}`}
                        data-testid={`mcp-category-${mc.key}`}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Checkbox
                              size="small"
                              sx={{ p: 0.5 }}
                              // Off with Sirius MCP itself: the categories
                              // narrow it, they cannot turn it on.
                              disabled={!checkedSet.has(ChatToolType.SiriusMcp)}
                              checked={
                                checkedSet.has(ChatToolType.SiriusMcp) &&
                                !excludedMcpCategories.includes(mc.key)
                              }
                              onClick={e => e.stopPropagation()}
                              onChange={() => toggleMcpCategory(mc.key)}
                            />
                            <Typography component="span" sx={{ fontSize: 11 }}>
                              {mc.title}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 10,
                                color: "text.secondary",
                                ml: 0.5,
                              }}
                            >
                              ({mc.tools.length})
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                </TreeItem>
              ))}
            </TreeItem>
          );
        })}
      </SimpleTreeView>
    </div>
  );
}
